import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import {
    Plus, Trash2, X, Save, Edit2, FileText, Printer, Image as ImageIcon,
    Database, ScrollText, Sparkles, Search, ChefHat, Eye, Download, Loader2, Palette,
    CheckCircle2, Power,
} from "lucide-react";

// ============================================================================
//  MENU DEL GIORNO + MASTER DISH DB + LOGO/BRAND
// ============================================================================
const COURSE_OPTIONS = [
    { value: "antipasto", label: "Antipasto" },
    { value: "primo", label: "Primo" },
    { value: "secondo", label: "Secondo" },
    { value: "contorno", label: "Contorno" },
    { value: "dolce", label: "Dolci" },
    { value: "bevanda", label: "Bevanda" },
    { value: "pizza", label: "Pizza" },
    { value: "altro", label: "Altro" },
];

const CATEGORY_DEFAULTS = ["Antipasti", "Primi", "Secondi", "Contorni", "Dolci", "Bevande", "Pizze"];

const MenuDelGiorno = () => {
    const [tab, setTab] = useState("daily"); // daily | dishes | brand

    return (
        <div className="space-y-6" data-testid="menu-del-giorno-page">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-brand mb-2">Carta · Menu del Giorno</div>
                    <h1 className="font-display text-4xl font-bold">Menu del Giorno</h1>
                    <p className="text-elegant text-sm mt-2 max-w-3xl">
                        Crea menu giornalieri pescando dal tuo database piatti. Esporta PDF eleganti pronti da stampare per i clienti
                        — con logo, allergeni e coperto.
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-[#1f2c44] overflow-x-auto">
                <TabButton active={tab === "daily"} onClick={() => setTab("daily")} icon={ScrollText} testid="tab-daily">
                    Menu del Giorno
                </TabButton>
                <TabButton active={tab === "dishes"} onClick={() => setTab("dishes")} icon={Database} testid="tab-dishes">
                    Database Piatti
                </TabButton>
                <TabButton active={tab === "brand"} onClick={() => setTab("brand")} icon={Palette} testid="tab-brand">
                    Logo & Brand
                </TabButton>
            </div>

            {tab === "daily" && <DailyMenusTab />}
            {tab === "dishes" && <DishesTab />}
            {tab === "brand" && <BrandTab />}
        </div>
    );
};

const TabButton = ({ active, onClick, icon: Icon, children, testid }) => (
    <button
        onClick={onClick}
        data-testid={testid}
        className={`px-4 py-3 text-sm uppercase tracking-wider font-display whitespace-nowrap flex items-center gap-2 border-b-2 -mb-px transition ${
            active
                ? "border-brand text-brand"
                : "border-transparent text-elegant hover:text-warm"
        }`}
    >
        <Icon className="w-4 h-4" />
        {children}
    </button>
);

// ============================================================================
// TAB 1 — DAILY MENUS
// ============================================================================
const DailyMenusTab = () => {
    const [menus, setMenus] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // { id?, name, date, notes, coperto, items: [] }
    const [showNew, setShowNew] = useState(false);
    const [activeMenuNames, setActiveMenuNames] = useState([]); // nomi menu attivi nei permanenti

    const load = async () => {
        try {
            setLoading(true);
            const [{ data: dm }, { data: pm }] = await Promise.all([
                api.get("/daily-menus"),
                api.get("/menus").catch(() => ({ data: [] })),
            ]);
            setMenus(dm || []);
            setActiveMenuNames((pm || []).filter((m) => m.active).map((m) => (m.name || "").trim()));
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const remove = async (m) => {
        if (!window.confirm(`Eliminare il menu "${m.name}"?`)) return;
        try {
            await api.delete(`/daily-menus/${m.id}`);
            toast.success("Menu eliminato");
            load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const activate = async (m) => {
        const msg = `Attivare "${m.name}" sui terminali?\n\nVerrà creato (o aggiornato) un menu in Gestione Menu con gli stessi piatti e impostato come attivo. Camerieri, clienti e cucina vedranno subito questo menu.`;
        if (!window.confirm(msg)) return;
        try {
            const { data } = await api.post(`/daily-menus/${m.id}/activate`);
            const verb = data.updated_existing ? "aggiornato e attivato" : "attivato";
            toast.success(`Menu "${m.name}" ${verb} (${data.items} piatti in ${data.categories} categorie)`);
            load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const copyToMenu = async (m) => {
        const msg = `Aggiungere i piatti di "${m.name}" al menu principale?\n\nI piatti nuovi verranno aggiunti (con le relative categorie). I piatti già presenti (stesso nome) NON saranno duplicati. Il tuo menu principale resta intatto.`;
        if (!window.confirm(msg)) return;
        try {
            const { data } = await api.post(`/daily-menus/${m.id}/copy-to-menu`);
            const parts = [];
            if (data.added_items) parts.push(`${data.added_items} piatti aggiunti`);
            if (data.added_categories) parts.push(`${data.added_categories} nuove categorie`);
            if (data.skipped_items) parts.push(`${data.skipped_items} già presenti (saltati)`);
            toast.success(`📥 ${parts.join(" · ") || "Nessuna modifica"} su "${data.menu_name}"`);
        } catch (e) { toast.error(formatError(e)); }
    };

    const openPdf = async (m) => {
        try {
            const res = await api.get(`/daily-menus/${m.id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank");
            // libera ObjectURL dopo che il browser ha avuto tempo di aprirlo
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (e) { toast.error(formatError(e)); }
    };

    const downloadPdf = async (m) => {
        try {
            const res = await api.get(`/daily-menus/${m.id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${(m.name || "menu").replace(/[^a-z0-9]+/gi, "_")}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 30_000);
        } catch (e) { toast.error(formatError(e)); }
    };

    const printPdf = async (m) => {
        try {
            const res = await api.get(`/daily-menus/${m.id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            // apre in finestra e tenta auto-print
            const w = window.open(url, "_blank");
            if (w) {
                w.addEventListener("load", () => {
                    try { w.focus(); w.print(); } catch (e) { /* no-op */ }
                });
            }
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (e) { toast.error(formatError(e)); }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-end">
                <button
                    onClick={() => setShowNew(true)}
                    data-testid="new-daily-menu-btn"
                    className="h-11 px-5 bg-brand hover:bg-brand-light text-deep font-bold rounded-md text-sm uppercase tracking-wider flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" /> Nuovo Menu del Giorno
                </button>
            </div>

            {loading ? (
                <div className="text-elegant text-sm">Caricamento...</div>
            ) : menus.length === 0 ? (
                <div className="bg-surface border border-[#1f2c44] rounded-lg p-10 text-center">
                    <ScrollText className="w-12 h-12 text-brand mx-auto mb-3 opacity-40" />
                    <div className="font-display text-xl text-warm mb-2">Nessun menu ancora creato</div>
                    <p className="text-elegant text-sm">Crea il primo menu del giorno con un click sul bottone in alto.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {menus.map((m) => {
                        const isLive = activeMenuNames.includes((m.name || "").trim());
                        return (
                        <div key={m.id} className={`bg-surface border rounded-lg p-5 hover:border-brand transition ${isLive ? "border-brand shadow-[0_0_0_1px_rgba(20,229,188,0.25)]" : "border-[#1f2c44]"}`} data-testid={`daily-menu-card-${m.id}`}>
                            <div className="flex items-start justify-between mb-3 gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <h3 className="font-display text-xl font-bold text-warm truncate">{m.name}</h3>
                                        {isLive && (
                                            <span className="text-[10px] uppercase tracking-wider bg-brand/15 text-brand border border-brand/40 px-2 py-0.5 rounded flex items-center gap-1 font-bold" data-testid={`live-badge-${m.id}`}>
                                                <CheckCircle2 className="w-3 h-3" /> Attivo sui terminali
                                            </span>
                                        )}
                                    </div>
                                    {m.date && (
                                        <div className="text-[11px] uppercase tracking-wider text-brand">
                                            {(() => {
                                                try {
                                                    return new Date(m.date).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
                                                } catch { return m.date; }
                                            })()}
                                        </div>
                                    )}
                                </div>
                                <div className="text-[10px] text-elegant uppercase tracking-wider whitespace-nowrap">{(m.items || []).length} piatti</div>
                            </div>
                            {m.notes && <p className="text-xs text-elegant italic mb-3 line-clamp-2">{m.notes}</p>}
                            {m.coperto > 0 && <div className="text-[11px] text-elegant mb-3">Coperto € {Number(m.coperto).toFixed(2)}</div>}

                            <button
                                onClick={() => activate(m)}
                                data-testid={`activate-menu-${m.id}`}
                                className={`w-full h-11 rounded-md text-xs uppercase tracking-wider font-bold flex items-center justify-center gap-2 mb-2 transition ${
                                    isLive
                                        ? "bg-brand/10 border border-brand text-brand hover:bg-brand/20"
                                        : "bg-brand text-deep hover:bg-brand-light"
                                }`}
                            >
                                <Power className="w-4 h-4" />
                                {isLive ? "Ri-attiva (aggiorna)" : "Attiva Menu"}
                            </button>

                            <button
                                onClick={() => copyToMenu(m)}
                                data-testid={`copy-to-menu-${m.id}`}
                                title="Aggiunge i piatti nuovi al menu principale senza sovrascrivere"
                                className="w-full h-10 rounded-md text-[11px] uppercase tracking-wider font-bold flex items-center justify-center gap-2 mb-2 border border-purple-500/50 text-purple-300 hover:bg-purple-900/20 transition"
                            >
                                <Download className="w-3.5 h-3.5" /> Aggiungi al Menu principale
                            </button>

                            <div className="flex flex-wrap gap-2 pt-3 border-t border-[#1f2c44]">
                                <button
                                    onClick={() => openPdf(m)}
                                    data-testid={`view-pdf-${m.id}`}
                                    className="flex-1 h-10 px-3 bg-brand/10 hover:bg-brand/20 text-brand border border-brand/40 rounded-md text-xs uppercase tracking-wider flex items-center justify-center gap-2 min-w-[120px]"
                                >
                                    <Eye className="w-3.5 h-3.5" /> Anteprima
                                </button>
                                <button
                                    onClick={() => printPdf(m)}
                                    data-testid={`print-pdf-${m.id}`}
                                    className="flex-1 h-10 px-3 bg-brand hover:bg-brand-light text-deep font-bold rounded-md text-xs uppercase tracking-wider flex items-center justify-center gap-2 min-w-[120px]"
                                >
                                    <Printer className="w-3.5 h-3.5" /> Stampa
                                </button>
                                <button
                                    onClick={() => downloadPdf(m)}
                                    data-testid={`download-pdf-${m.id}`}
                                    className="h-10 w-10 bg-deep border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand rounded-md flex items-center justify-center"
                                    title="Scarica PDF"
                                >
                                    <Download className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => setEditing(m)}
                                    data-testid={`edit-${m.id}`}
                                    className="h-10 w-10 bg-deep border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand rounded-md flex items-center justify-center"
                                    title="Modifica"
                                >
                                    <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => remove(m)}
                                    data-testid={`delete-${m.id}`}
                                    className="h-10 w-10 bg-deep border border-[#1f2c44] hover:border-red-500/60 text-elegant hover:text-red-400 rounded-md flex items-center justify-center"
                                    title="Elimina"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}

            {(showNew || editing) && (
                <DailyMenuEditor
                    initial={editing}
                    onClose={() => { setShowNew(false); setEditing(null); }}
                    onSaved={() => { setShowNew(false); setEditing(null); load(); }}
                />
            )}
        </div>
    );
};

// ============================================================================
// DAILY MENU EDITOR MODAL
// ============================================================================
const DailyMenuEditor = ({ initial, onClose, onSaved }) => {
    const isEdit = !!initial?.id;
    const [name, setName] = useState(initial?.name || "");
    const [date, setDate] = useState(initial?.date || new Date().toISOString().slice(0, 10));
    const [notes, setNotes] = useState(initial?.notes || "");
    const [coperto, setCoperto] = useState(initial?.coperto || 0);
    const [items, setItems] = useState(initial?.items || []);
    const [categoryTitles, setCategoryTitles] = useState(initial?.category_titles || {});
    const [categoryOrder, setCategoryOrder] = useState(initial?.category_order || []);
    const [printDrinks, setPrintDrinks] = useState(initial?.print_drinks !== false);
    const [textSize, setTextSize] = useState(initial?.text_size || "medium");
    const [saving, setSaving] = useState(false);

    // All'apertura di un NUOVO menu, auto-precarica tutte le bevande dal Master DB
    useEffect(() => {
        if (isEdit) return;
        (async () => {
            try {
                const { data } = await api.get("/menu/items", { params: { all: true } });
                const drinks = (data || []).filter((d) => d.course === "bevanda" && d.available !== false);
                if (drinks.length === 0) return;
                setItems((prev) => {
                    const base = prev || [];
                    const adds = drinks.map((d, i) => ({
                        menu_item_id: d.id,
                        name: d.name,
                        description: d.description || "",
                        price: Number(d.price || 0),
                        category: "Bevande",
                        allergens: d.allergens || [],
                        order: base.length + i,
                    }));
                    return [...base, ...adds];
                });
            } catch { /* no-op */ }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [showDishPicker, setShowDishPicker] = useState(false);
    const [showFreeForm, setShowFreeForm] = useState(false);

    const addDishesFromMaster = (selected) => {
        // selected: array di menu_item objects
        const baseOrder = items.length;
        const adds = selected.map((d, i) => ({
            menu_item_id: d.id,
            name: d.name,
            description: d.description || "",
            price: Number(d.price || 0),
            category: mapCourseToCategory(d.course),
            allergens: d.allergens || [],
            order: baseOrder + i,
        }));
        setItems([...items, ...adds]);
        setShowDishPicker(false);
    };

    const addFreeItem = (it) => {
        setItems([...items, { ...it, order: items.length }]);
        setShowFreeForm(false);
    };

    const updateItem = (idx, patch) => {
        setItems(items.map((it, i) => i === idx ? { ...it, ...patch } : it));
    };

    const removeItem = (idx) => {
        setItems(items.filter((_, i) => i !== idx));
    };

    const moveItem = (idx, dir) => {
        const newIdx = idx + dir;
        if (newIdx < 0 || newIdx >= items.length) return;
        const copy = [...items];
        [copy[idx], copy[newIdx]] = [copy[newIdx], copy[idx]];
        setItems(copy.map((it, i) => ({ ...it, order: i })));
    };

    const save = async () => {
        if (!name.trim()) { toast.error("Inserisci un nome per il menu"); return; }
        try {
            setSaving(true);
            const payload = {
                name: name.trim(),
                date: date || "",
                notes: notes || "",
                coperto: Number(coperto) || 0,
                category_titles: categoryTitles,
                category_order: orderedCategoryNames,
                print_drinks: printDrinks,
                text_size: textSize,
                items: items.map((it, i) => ({
                    menu_item_id: it.menu_item_id || null,
                    name: it.name || "",
                    description: it.description || "",
                    price: Number(it.price) || 0,
                    category: it.category || "Piatti",
                    allergens: it.allergens || [],
                    order: i,
                })),
            };
            if (isEdit) {
                await api.put(`/daily-menus/${initial.id}`, payload);
                toast.success("Menu aggiornato");
            } else {
                await api.post("/daily-menus", payload);
                toast.success("Menu creato");
            }
            onSaved();
        } catch (e) { toast.error(formatError(e)); }
        finally { setSaving(false); }
    };

    // raggruppa per categoria per la visualizzazione
    const grouped = useMemo(() => {
        const g = {};
        items.forEach((it, idx) => {
            const cat = it.category || "Piatti";
            if (!g[cat]) g[cat] = [];
            g[cat].push({ ...it, idx });
        });
        return g;
    }, [items]);

    // Ordine canonico fallback per categorie nuove (Antipasti → Primi → ...)
    const CAT_RANK = { "Antipasti": 1, "Primi": 2, "Secondi": 3, "Contorni": 4, "Dolci": 5, "Bevande": 6, "Pizze": 7 };

    // Lista finale delle categorie nell'ordine desiderato di stampa
    const orderedCategoryNames = useMemo(() => {
        const present = Object.keys(grouped);
        const inCustom = categoryOrder.filter((c) => present.includes(c));
        const others = present.filter((c) => !inCustom.includes(c)).sort((a, b) => (CAT_RANK[a] || 99) - (CAT_RANK[b] || 99) || a.localeCompare(b));
        return [...inCustom, ...others];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [grouped, categoryOrder]);

    const moveCategory = (cat, dir) => {
        const list = [...orderedCategoryNames];
        const i = list.indexOf(cat);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= list.length) return;
        [list[i], list[j]] = [list[j], list[i]];
        setCategoryOrder(list);
    };

    return (
        <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4 overflow-y-auto" data-testid="daily-menu-editor">
            <div className="bg-deep border border-[#1f2c44] rounded-lg w-full max-w-4xl my-8 max-h-[92vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#1f2c44] flex-shrink-0">
                    <h2 className="font-display text-2xl font-bold text-warm">
                        {isEdit ? "Modifica Menu" : "Nuovo Menu del Giorno"}
                    </h2>
                    <button onClick={onClose} className="text-elegant hover:text-warm" data-testid="close-editor">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                            <Label>Nome del menu *</Label>
                            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="es. Menu della Domenica" testid="daily-menu-name" />
                        </div>
                        <div>
                            <Label>Data</Label>
                            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} testid="menu-date" />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                            <Label>Note (mostrate in fondo al PDF)</Label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={2}
                                placeholder="es. Pesce fresco di giornata · Possibilità di abbinamento vini"
                                className="w-full bg-deep border border-[#1f2c44] focus:border-brand text-warm rounded-md px-3 py-2 text-sm outline-none"
                                data-testid="menu-notes"
                            />
                        </div>
                        <div>
                            <Label>Coperto (€/persona)</Label>
                            <Input
                                type="number"
                                step="0.5"
                                min="0"
                                value={coperto}
                                onChange={(e) => setCoperto(e.target.value)}
                                testid="menu-coperto"
                            />
                        </div>
                    </div>

                    {/* Opzioni stampa: dimensione testo + toggle bevande */}
                    <div className="bg-surface/50 border border-[#1f2c44] rounded-md p-3 flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                            <span className="text-[11px] uppercase tracking-wider text-elegant whitespace-nowrap">Dimensione testo PDF</span>
                            <div className="inline-flex rounded-md overflow-hidden border border-[#1f2c44]" data-testid="text-size-toggle">
                                {[
                                    { v: "small", label: "S" },
                                    { v: "medium", label: "M" },
                                    { v: "large", label: "L" },
                                ].map((opt) => (
                                    <button
                                        key={opt.v}
                                        type="button"
                                        onClick={() => setTextSize(opt.v)}
                                        data-testid={`text-size-${opt.v}`}
                                        className={`px-3 h-9 text-xs font-bold uppercase tracking-wider border-r border-[#1f2c44] last:border-r-0 transition ${
                                            textSize === opt.v
                                                ? "bg-brand text-deep"
                                                : "bg-deep text-elegant hover:text-brand"
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                            <span className="text-[10px] text-elegant">Compatto / Standard / Grande</span>
                        </div>
                    </div>

                    {/* Bevande: toggle stampa + auto-import */}
                    <div className="bg-surface/50 border border-[#1f2c44] rounded-md p-3 flex items-center justify-between flex-wrap gap-3">
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={printDrinks}
                                onChange={(e) => setPrintDrinks(e.target.checked)}
                                className="accent-brand w-4 h-4"
                                data-testid="toggle-print-drinks"
                            />
                            <div>
                                <div className="text-sm font-bold text-warm">Stampa Bevande sul PDF</div>
                                <div className="text-[11px] text-elegant">Se disattivato, la sezione Bevande viene esclusa dal PDF stampato (resta visibile sui terminali).</div>
                            </div>
                        </label>
                        <button
                            type="button"
                            onClick={async () => {
                                try {
                                    const { data } = await api.get("/menu/items", { params: { all: true } });
                                    const drinks = (data || []).filter((d) => d.course === "bevanda" && d.available !== false);
                                    if (drinks.length === 0) { toast("Nessuna bevanda nel Database Piatti"); return; }
                                    const existingIds = new Set(items.map((it) => it.menu_item_id).filter(Boolean));
                                    const adds = drinks
                                        .filter((d) => !existingIds.has(d.id))
                                        .map((d, i) => ({
                                            menu_item_id: d.id,
                                            name: d.name,
                                            description: d.description || "",
                                            price: Number(d.price || 0),
                                            category: "Bevande",
                                            allergens: d.allergens || [],
                                            order: items.length + i,
                                        }));
                                    if (adds.length === 0) { toast("Tutte le bevande sono già nel menu"); return; }
                                    setItems([...items, ...adds]);
                                    toast.success(`${adds.length} bevande aggiunte`);
                                } catch (e) { toast.error(formatError(e)); }
                            }}
                            data-testid="add-all-drinks"
                            className="h-9 px-3 border border-brand/40 hover:border-brand text-brand hover:bg-brand/5 rounded-md text-xs uppercase tracking-wider flex items-center gap-2"
                        >
                            + Tutte le bevande
                        </button>
                    </div>

                    {/* Items section */}
                    <div className="border-t border-[#1f2c44] pt-5">
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                            <h3 className="font-display text-lg font-bold text-warm">Piatti del menu ({items.length})</h3>
                            <div className="flex gap-2 flex-wrap">
                                <button
                                    onClick={() => setShowDishPicker(true)}
                                    data-testid="add-from-master"
                                    className="h-10 px-4 bg-brand hover:bg-brand-light text-deep font-bold rounded-md text-xs uppercase tracking-wider flex items-center gap-2"
                                >
                                    <Database className="w-3.5 h-3.5" /> Da Database
                                </button>
                                <button
                                    onClick={() => setShowFreeForm(true)}
                                    data-testid="add-free-item"
                                    className="h-10 px-4 bg-deep border border-brand/40 hover:border-brand text-brand rounded-md text-xs uppercase tracking-wider flex items-center gap-2"
                                >
                                    <Plus className="w-3.5 h-3.5" /> Nuovo Piatto
                                </button>
                            </div>
                        </div>

                        {items.length === 0 ? (
                            <div className="bg-surface border border-dashed border-[#1f2c44] rounded-lg p-8 text-center text-elegant text-sm">
                                Nessun piatto. Aggiungi piatti dal database o creane di nuovi.
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {orderedCategoryNames.map((cat, catIdx) => {
                                    const list = grouped[cat] || [];
                                    return (
                                    <div key={cat}>
                                        <div className="flex items-center gap-2 mb-2">
                                            <div className="flex flex-col gap-0 mr-1">
                                                <button
                                                    onClick={() => moveCategory(cat, -1)}
                                                    disabled={catIdx === 0}
                                                    title="Porta su"
                                                    data-testid={`cat-up-${cat}`}
                                                    className="text-elegant hover:text-brand text-xs px-1.5 py-0 disabled:opacity-25 disabled:cursor-not-allowed"
                                                >▲</button>
                                                <button
                                                    onClick={() => moveCategory(cat, 1)}
                                                    disabled={catIdx === orderedCategoryNames.length - 1}
                                                    title="Porta giù"
                                                    data-testid={`cat-down-${cat}`}
                                                    className="text-elegant hover:text-brand text-xs px-1.5 py-0 disabled:opacity-25 disabled:cursor-not-allowed"
                                                >▼</button>
                                            </div>
                                            <span className="text-[10px] uppercase tracking-[0.3em] text-elegant whitespace-nowrap">{cat} →</span>
                                            <input
                                                type="text"
                                                value={categoryTitles[cat] ?? cat}
                                                onChange={(e) => setCategoryTitles({ ...categoryTitles, [cat]: e.target.value })}
                                                placeholder={cat}
                                                title="Personalizza il titolo che apparirà sul PDF (es. 'Antipasti di mare')"
                                                data-testid={`cat-title-${cat}`}
                                                className="flex-1 bg-transparent border-b border-[#1f2c44] focus:border-brand text-brand font-display text-base px-1 py-0.5 outline-none uppercase tracking-[0.25em]"
                                            />
                                            {categoryTitles[cat] !== undefined && categoryTitles[cat] !== cat && (
                                                <button
                                                    onClick={() => { const c = { ...categoryTitles }; delete c[cat]; setCategoryTitles(c); }}
                                                    className="text-[10px] text-elegant hover:text-brand"
                                                    title="Ripristina titolo predefinito"
                                                >
                                                    ↺
                                                </button>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            {list.map((it) => (
                                                <div key={it.idx} className="bg-surface border border-[#1f2c44] rounded-md p-3 flex items-start gap-3" data-testid={`item-row-${it.idx}`}>
                                                    <div className="flex flex-col gap-0.5">
                                                        <button onClick={() => moveItem(it.idx, -1)} className="text-elegant hover:text-brand text-xs px-1.5 py-0.5" title="Su">▲</button>
                                                        <button onClick={() => moveItem(it.idx, 1)} className="text-elegant hover:text-brand text-xs px-1.5 py-0.5" title="Giù">▼</button>
                                                    </div>
                                                    <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-12 gap-2">
                                                        <input
                                                            value={it.name}
                                                            onChange={(e) => updateItem(it.idx, { name: e.target.value })}
                                                            placeholder="Nome piatto"
                                                            className="md:col-span-4 bg-deep border border-[#1f2c44] focus:border-brand text-warm rounded px-2 py-1.5 text-sm outline-none"
                                                        />
                                                        <input
                                                            value={it.description}
                                                            onChange={(e) => updateItem(it.idx, { description: e.target.value })}
                                                            placeholder="Descrizione"
                                                            className="md:col-span-5 bg-deep border border-[#1f2c44] focus:border-brand text-elegant rounded px-2 py-1.5 text-xs outline-none italic"
                                                        />
                                                        <input
                                                            type="number"
                                                            step="0.5"
                                                            value={it.price}
                                                            onChange={(e) => updateItem(it.idx, { price: e.target.value })}
                                                            placeholder="Prezzo"
                                                            className="md:col-span-2 bg-deep border border-[#1f2c44] focus:border-brand text-warm rounded px-2 py-1.5 text-sm outline-none text-right"
                                                        />
                                                        <select
                                                            value={it.category}
                                                            onChange={(e) => updateItem(it.idx, { category: e.target.value })}
                                                            className="md:col-span-1 bg-deep border border-[#1f2c44] focus:border-brand text-warm rounded px-1 py-1.5 text-xs outline-none"
                                                            title="Categoria nel menu"
                                                        >
                                                            {CATEGORY_DEFAULTS.map((c) => <option key={c} value={c}>{c}</option>)}
                                                        </select>
                                                    </div>
                                                    <button
                                                        onClick={() => removeItem(it.idx)}
                                                        className="text-elegant hover:text-red-400 p-1"
                                                        title="Rimuovi"
                                                        data-testid={`remove-item-${it.idx}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-[#1f2c44] flex justify-end gap-2 flex-shrink-0">
                    <button onClick={onClose} className="h-11 px-5 border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand rounded-md text-sm uppercase tracking-wider">
                        Annulla
                    </button>
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="save-daily-menu"
                        className="h-11 px-6 bg-brand hover:bg-brand-light text-deep font-bold rounded-md text-sm uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {isEdit ? "Salva modifiche" : "Crea menu"}
                    </button>
                </div>

                {showDishPicker && (
                    <DishPickerModal
                        onClose={() => setShowDishPicker(false)}
                        onPick={addDishesFromMaster}
                    />
                )}
                {showFreeForm && (
                    <FreeItemModal
                        onClose={() => setShowFreeForm(false)}
                        onAdd={addFreeItem}
                    />
                )}
            </div>
        </div>
    );
};

const mapCourseToCategory = (course) => {
    const m = {
        antipasto: "Antipasti",
        primo: "Primi",
        secondo: "Secondi",
        contorno: "Contorni",
        dolce: "Dolci",
        dessert: "Dolci",
        bevanda: "Bevande",
        pizza: "Pizze",
    };
    return m[course] || "Piatti";
};

// ============================================================================
// DISH PICKER MODAL — scegli piatti dal Master DB
// ============================================================================
const DishPickerModal = ({ onClose, onPick }) => {
    const [dishes, setDishes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [courseFilter, setCourseFilter] = useState("all");
    const [selected, setSelected] = useState({}); // id -> true

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const { data } = await api.get("/menu/items", { params: { all: true } });
                setDishes(data || []);
            } catch (e) { toast.error(formatError(e)); }
            finally { setLoading(false); }
        })();
    }, []);

    const filtered = useMemo(() => {
        const s = search.trim().toLowerCase();
        return dishes.filter((d) => {
            if (courseFilter !== "all" && d.course !== courseFilter) return false;
            if (!s) return true;
            return (d.name || "").toLowerCase().includes(s) ||
                   (d.description || "").toLowerCase().includes(s);
        });
    }, [dishes, search, courseFilter]);

    const toggle = (id) => setSelected({ ...selected, [id]: !selected[id] });

    const confirm = () => {
        const picked = dishes.filter((d) => selected[d.id]);
        if (picked.length === 0) { toast.error("Seleziona almeno un piatto"); return; }
        onPick(picked);
    };

    return (
        <div className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4">
            <div className="bg-deep border border-[#1f2c44] rounded-lg w-full max-w-3xl max-h-[85vh] flex flex-col">
                <div className="px-5 py-3 border-b border-[#1f2c44] flex items-center justify-between">
                    <h3 className="font-display text-lg font-bold text-warm">Scegli dal Database Piatti</h3>
                    <button onClick={onClose} className="text-elegant hover:text-warm"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-4 border-b border-[#1f2c44] flex flex-wrap gap-3">
                    <div className="relative flex-1 min-w-[180px]">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-elegant" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Cerca piatto..."
                            className="w-full bg-surface border border-[#1f2c44] focus:border-brand text-warm rounded-md pl-10 pr-3 py-2 text-sm outline-none"
                            data-testid="dish-search"
                        />
                    </div>
                    <select
                        value={courseFilter}
                        onChange={(e) => setCourseFilter(e.target.value)}
                        className="h-10 bg-surface border border-[#1f2c44] focus:border-brand text-warm rounded-md px-3 text-sm outline-none"
                        data-testid="picker-course-filter"
                    >
                        <option value="all">Tutte le tipologie</option>
                        {COURSE_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                </div>
                <div className="flex-1 overflow-y-auto p-3">
                    {loading ? (
                        <div className="text-elegant text-sm p-4">Caricamento...</div>
                    ) : filtered.length === 0 ? (
                        <div className="text-elegant text-sm p-4 text-center">
                            {dishes.length === 0 ? "Database piatti vuoto. Crea piatti dal tab 'Database Piatti'." : "Nessun risultato."}
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {filtered.map((d) => {
                                const noAllergens = !d.allergens || d.allergens.length === 0;
                                return (
                                <label
                                    key={d.id}
                                    className={`flex items-start gap-3 p-2.5 rounded-md cursor-pointer transition border ${
                                        selected[d.id] ? "bg-brand/15 border-brand" : "bg-surface border-transparent hover:border-[#1f2c44]"
                                    }`}
                                    data-testid={`dish-pick-${d.id}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={!!selected[d.id]}
                                        onChange={() => toggle(d.id)}
                                        className="mt-1 accent-brand"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-2 flex-wrap">
                                            <div className="font-display text-warm font-bold truncate">{d.name}</div>
                                            <div className="text-brand text-sm whitespace-nowrap">€ {Number(d.price || 0).toFixed(2)}</div>
                                        </div>
                                        {d.description && <div className="text-elegant text-xs italic line-clamp-2">{d.description}</div>}
                                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                                            <span className="text-[10px] uppercase tracking-wider text-elegant">{d.course || "—"}</span>
                                            {noAllergens && (
                                                <span className="text-[10px] uppercase tracking-wider bg-amber-500/20 text-amber-400 border border-amber-500/40 px-1.5 py-0.5 rounded font-bold" data-testid={`picker-no-allergens-${d.id}`}>
                                                    ⚠ Allergeni mancanti
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </label>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className="px-5 py-3 border-t border-[#1f2c44] flex justify-between items-center">
                    <div className="text-xs text-elegant">{Object.values(selected).filter(Boolean).length} selezionati</div>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="h-10 px-4 border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand rounded-md text-xs uppercase tracking-wider">
                            Annulla
                        </button>
                        <button
                            onClick={confirm}
                            data-testid="confirm-picks"
                            className="h-10 px-5 bg-brand hover:bg-brand-light text-deep font-bold rounded-md text-xs uppercase tracking-wider"
                        >
                            Aggiungi al menu
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// FREE ITEM MODAL — nuovo piatto senza salvarlo nel master DB
// ============================================================================
const FreeItemModal = ({ onClose, onAdd }) => {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [price, setPrice] = useState(0);
    const [category, setCategory] = useState("Piatti");
    const [saveToDB, setSaveToDB] = useState(true);
    const [saving, setSaving] = useState(false);
    const [allergens, setAllergens] = useState([]);
    const [allergenOptions, setAllergenOptions] = useState([]);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const [{ data: a }, { data: c }] = await Promise.all([
                    api.get("/allergens"),
                    api.get("/menu/categories"),
                ]);
                setAllergenOptions(a || []);
                setCategories(c || []);
            } catch { /* no-op */ }
        })();
    }, []);

    const toggleAllergen = (id) => {
        setAllergens(allergens.includes(id) ? allergens.filter((x) => x !== id) : [...allergens, id]);
    };

    const add = async () => {
        if (!name.trim()) { toast.error("Inserisci il nome del piatto"); return; }
        let menu_item_id = null;
        if (saveToDB) {
            try {
                setSaving(true);
                // crea nel master DB (default course = primo)
                const courseGuess = guessCourse(category);
                const catObj = categories.find((c) => c.name === category);
                let category_id = catObj?.id;
                if (!category_id && categories.length > 0) category_id = categories[0].id;
                const payload = {
                    name: name.trim(),
                    description: description || "",
                    price: Number(price) || 0,
                    allergens,
                    available: true,
                    course: courseGuess,
                    category_id: category_id || "",
                };
                const { data } = await api.post("/menu/items", payload);
                menu_item_id = data.id;
                toast.success("Piatto salvato nel database");
            } catch (e) {
                toast.error("Impossibile salvare nel DB: " + formatError(e));
                setSaving(false);
                return;
            } finally { setSaving(false); }
        }
        onAdd({
            menu_item_id,
            name: name.trim(),
            description,
            price: Number(price) || 0,
            category,
            allergens,
        });
    };

    return (
        <div className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4">
            <div className="bg-deep border border-[#1f2c44] rounded-lg w-full max-w-xl max-h-[90vh] flex flex-col">
                <div className="px-5 py-3 border-b border-[#1f2c44] flex items-center justify-between">
                    <h3 className="font-display text-lg font-bold text-warm">Nuovo piatto</h3>
                    <button onClick={onClose} className="text-elegant hover:text-warm"><X className="w-5 h-5" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    <div>
                        <Label>Nome *</Label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} testid="free-item-name" />
                    </div>
                    <div>
                        <Label>Descrizione</Label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={2}
                            className="w-full bg-surface border border-[#1f2c44] focus:border-brand text-warm rounded-md px-3 py-2 text-sm outline-none"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Prezzo (€)</Label>
                            <Input type="number" step="0.5" value={price} onChange={(e) => setPrice(e.target.value)} testid="free-item-price" />
                        </div>
                        <div>
                            <Label>Categoria nel menu</Label>
                            <select
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                className="w-full h-10 bg-surface border border-[#1f2c44] focus:border-brand text-warm rounded-md px-3 text-sm outline-none"
                            >
                                {CATEGORY_DEFAULTS.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                    </div>
                    {allergenOptions.length > 0 && (
                        <div>
                            <Label>Allergeni</Label>
                            <div className="flex flex-wrap gap-2">
                                {allergenOptions.map((a) => (
                                    <button
                                        key={a.id}
                                        type="button"
                                        onClick={() => toggleAllergen(a.id)}
                                        className={`text-xs px-2.5 py-1 rounded-md border transition ${
                                            allergens.includes(a.id)
                                                ? "bg-brand text-deep border-brand font-bold"
                                                : "bg-surface text-elegant border-[#1f2c44] hover:border-brand"
                                        }`}
                                    >
                                        {a.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    <label className="flex items-center gap-2 text-sm text-elegant cursor-pointer">
                        <input
                            type="checkbox"
                            checked={saveToDB}
                            onChange={(e) => setSaveToDB(e.target.checked)}
                            className="accent-brand"
                            data-testid="save-to-db"
                        />
                        Salva anche nel Database Piatti (riutilizzabile)
                    </label>
                </div>
                <div className="px-5 py-3 border-t border-[#1f2c44] flex justify-end gap-2">
                    <button onClick={onClose} className="h-10 px-4 border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand rounded-md text-xs uppercase tracking-wider">Annulla</button>
                    <button
                        onClick={add}
                        disabled={saving}
                        data-testid="confirm-free-item"
                        className="h-10 px-5 bg-brand hover:bg-brand-light text-deep font-bold rounded-md text-xs uppercase tracking-wider disabled:opacity-50"
                    >
                        {saving ? "Salvataggio..." : "Aggiungi"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const guessCourse = (category) => {
    const m = {
        "Antipasti": "antipasto",
        "Primi": "primo",
        "Secondi": "secondo",
        "Contorni": "contorno",
        "Dolci": "dolce",
        "Bevande": "bevanda",
        "Pizze": "pizza",
    };
    return m[category] || "primo";
};

// ============================================================================
// TAB 2 — MASTER DB DISHES
// ============================================================================
const DishesTab = () => {
    const [dishes, setDishes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("all"); // course filter
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/menu/items", { params: { all: true } });
            setDishes(data || []);
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const s = search.trim().toLowerCase();
        return dishes.filter((d) => {
            if (filter !== "all" && d.course !== filter) return false;
            if (!s) return true;
            return (d.name || "").toLowerCase().includes(s) ||
                   (d.description || "").toLowerCase().includes(s);
        });
    }, [dishes, search, filter]);

    const remove = async (d) => {
        if (!window.confirm(`Eliminare "${d.name}" dal database piatti?`)) return;
        try {
            await api.delete(`/menu/items/${d.id}`);
            toast.success("Piatto eliminato");
            load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const toggleAvailable = async (d) => {
        try {
            await api.patch(`/menu/items/${d.id}`, { available: !d.available });
            toast.success(!d.available ? `"${d.name}" riattivato` : `"${d.name}" segnato TERMINATO`);
            load();
        } catch (e) { toast.error(formatError(e)); }
    };

    return (
        <div className="space-y-4">
            <div className="bg-surface border border-[#1f2c44] rounded-lg p-4 flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-elegant" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Cerca per nome o descrizione..."
                        className="w-full bg-deep border border-[#1f2c44] focus:border-brand text-warm rounded-md pl-10 pr-3 py-2 text-sm outline-none"
                        data-testid="dishes-search"
                    />
                </div>
                <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="h-10 bg-deep border border-[#1f2c44] focus:border-brand text-warm rounded-md px-3 text-sm outline-none"
                    data-testid="dishes-filter"
                >
                    <option value="all">Tutte le portate</option>
                    {COURSE_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <button
                    onClick={() => setCreating(true)}
                    data-testid="new-dish-btn"
                    className="h-10 px-4 bg-brand hover:bg-brand-light text-deep font-bold rounded-md text-xs uppercase tracking-wider flex items-center gap-2"
                >
                    <Plus className="w-3.5 h-3.5" /> Nuovo piatto
                </button>
            </div>

            <div className="text-xs text-elegant">
                Database Piatti del ristorante · {filtered.length} di {dishes.length} piatti
                {dishes.length > 0 && filtered.length === 0 && <span className="ml-2 text-warm">Nessun risultato per i filtri attivi</span>}
            </div>

            {loading ? (
                <div className="text-elegant text-sm">Caricamento...</div>
            ) : filtered.length === 0 && dishes.length === 0 ? (
                <div className="bg-surface border border-[#1f2c44] rounded-lg p-10 text-center">
                    <ChefHat className="w-12 h-12 text-brand mx-auto mb-3 opacity-40" />
                    <div className="font-display text-xl text-warm mb-2">Database piatti vuoto</div>
                    <p className="text-elegant text-sm mb-4">Crea i tuoi piatti qui per riutilizzarli ogni giorno nei menu.</p>
                </div>
            ) : (
                <div className="bg-surface border border-[#1f2c44] rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-deep border-b border-[#1f2c44]">
                            <tr className="text-left text-[11px] uppercase tracking-wider text-elegant">
                                <th className="px-4 py-3">Nome</th>
                                <th className="px-4 py-3 hidden md:table-cell">Portata</th>
                                <th className="px-4 py-3 text-right">Prezzo</th>
                                <th className="px-4 py-3 hidden lg:table-cell">Allergeni</th>
                                <th className="px-4 py-3 w-32 text-right">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((d) => {
                                const noAllergens = !d.allergens || d.allergens.length === 0;
                                const terminato = d.available === false;
                                return (
                                <tr key={d.id} className={`border-b border-[#1f2c44] hover:bg-deep/50 ${terminato ? "opacity-60" : ""}`} data-testid={`dish-row-${d.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <div className={`font-display font-bold ${terminato ? "text-elegant line-through" : "text-warm"}`}>{d.name}</div>
                                            {terminato && (
                                                <span className="text-[10px] uppercase tracking-wider bg-red-600 text-white px-1.5 py-0.5 rounded font-bold" data-testid={`row-terminato-${d.id}`}>TERMINATO</span>
                                            )}
                                            {noAllergens && (
                                                <span className="text-[10px] uppercase tracking-wider bg-amber-500/20 text-amber-400 border border-amber-500/40 px-1.5 py-0.5 rounded font-bold" data-testid={`row-no-allergens-${d.id}`}>
                                                    ⚠ Compilare allergeni
                                                </span>
                                            )}
                                        </div>
                                        {d.description && <div className="text-xs text-elegant italic line-clamp-1">{d.description}</div>}
                                    </td>
                                    <td className="px-4 py-3 hidden md:table-cell text-elegant text-xs uppercase tracking-wider">{d.course || "—"}</td>
                                    <td className="px-4 py-3 text-right text-brand font-bold whitespace-nowrap">€ {Number(d.price || 0).toFixed(2)}</td>
                                    <td className="px-4 py-3 hidden lg:table-cell text-xs text-elegant">
                                        {noAllergens ? <span className="text-amber-400">— da compilare</span> : (d.allergens || []).join(", ")}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex gap-1">
                                            <button
                                                onClick={() => toggleAvailable(d)}
                                                className={`h-8 px-2 text-[10px] uppercase tracking-wider rounded border font-bold transition ${
                                                    terminato
                                                        ? "border-green-500/50 text-green-400 hover:bg-green-500/10"
                                                        : "border-red-500/50 text-red-400 hover:bg-red-500/10"
                                                }`}
                                                data-testid={`toggle-available-${d.id}`}
                                                title={terminato ? "Riattiva" : "Segna come terminato"}
                                            >
                                                {terminato ? "Riattiva" : "Termina"}
                                            </button>
                                            <button onClick={() => setEditing(d)} className="h-8 w-8 text-elegant hover:text-brand flex items-center justify-center" title="Modifica" data-testid={`edit-dish-${d.id}`}>
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => remove(d)} className="h-8 w-8 text-elegant hover:text-red-400 flex items-center justify-center" title="Elimina" data-testid={`delete-dish-${d.id}`}>
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {(creating || editing) && (
                <DishEditor
                    initial={editing}
                    onClose={() => { setCreating(false); setEditing(null); }}
                    onSaved={() => { setCreating(false); setEditing(null); load(); }}
                />
            )}
        </div>
    );
};

// ============================================================================
// DISH EDITOR — crea/edit master dish
// ============================================================================
const DishEditor = ({ initial, onClose, onSaved }) => {
    const isEdit = !!initial?.id;
    const [name, setName] = useState(initial?.name || "");
    const [description, setDescription] = useState(initial?.description || "");
    const [price, setPrice] = useState(initial?.price || 0);
    const [course, setCourse] = useState(initial?.course || "primo");
    const [allergens, setAllergens] = useState(initial?.allergens || []);
    const [available, setAvailable] = useState(initial?.available !== false);
    const [allergenOptions, setAllergenOptions] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoryId, setCategoryId] = useState(initial?.category_id || "");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [{ data: a }, { data: c }] = await Promise.all([
                    api.get("/allergens"),
                    api.get("/menu/categories"),
                ]);
                setAllergenOptions(a || []);
                setCategories(c || []);
                if (!categoryId && c?.length > 0) setCategoryId(c[0].id);
            } catch { /* no-op */ }
        })();
    }, []);

    const toggleAllergen = (id) => {
        setAllergens(allergens.includes(id) ? allergens.filter((x) => x !== id) : [...allergens, id]);
    };

    const save = async () => {
        if (!name.trim()) { toast.error("Nome obbligatorio"); return; }
        if (!categoryId) { toast.error("Crea prima una categoria menu (in Gestione Menu)"); return; }
        if (allergens.length === 0) {
            if (!window.confirm("ATTENZIONE: nessun allergene selezionato.\n\nGli allergeni sono obbligatori per legge sui menu stampati. Vuoi continuare comunque?")) {
                return;
            }
        }
        try {
            setSaving(true);
            const payload = {
                name: name.trim(),
                description: description || "",
                price: Number(price) || 0,
                course,
                allergens,
                available,
                category_id: categoryId,
            };
            if (isEdit) {
                await api.patch(`/menu/items/${initial.id}`, payload);
                toast.success("Piatto aggiornato");
            } else {
                await api.post("/menu/items", payload);
                toast.success("Piatto creato");
            }
            onSaved();
        } catch (e) { toast.error(formatError(e)); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-deep border border-[#1f2c44] rounded-lg w-full max-w-xl my-8 max-h-[90vh] flex flex-col">
                <div className="px-5 py-3 border-b border-[#1f2c44] flex items-center justify-between">
                    <h3 className="font-display text-lg font-bold text-warm">{isEdit ? "Modifica piatto" : "Nuovo piatto"}</h3>
                    <button onClick={onClose} className="text-elegant hover:text-warm"><X className="w-5 h-5" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    <div>
                        <Label>Nome *</Label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} testid="dish-name" />
                    </div>
                    <div>
                        <Label>Descrizione</Label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={2}
                            className="w-full bg-surface border border-[#1f2c44] focus:border-brand text-warm rounded-md px-3 py-2 text-sm outline-none"
                            data-testid="dish-description"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Prezzo (€) *</Label>
                            <Input type="number" step="0.5" value={price} onChange={(e) => setPrice(e.target.value)} testid="dish-price" />
                        </div>
                        <div>
                            <Label>Portata</Label>
                            <select
                                value={course}
                                onChange={(e) => setCourse(e.target.value)}
                                className="w-full h-10 bg-surface border border-[#1f2c44] focus:border-brand text-warm rounded-md px-3 text-sm outline-none"
                                data-testid="dish-course"
                            >
                                {COURSE_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                        </div>
                    </div>
                    {categories.length > 0 && (
                        <div>
                            <Label>Categoria menu (per ordine cameriere)</Label>
                            <select
                                value={categoryId}
                                onChange={(e) => setCategoryId(e.target.value)}
                                className="w-full h-10 bg-surface border border-[#1f2c44] focus:border-brand text-warm rounded-md px-3 text-sm outline-none"
                            >
                                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                    )}
                    {allergenOptions.length > 0 && (
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label className="text-xs uppercase tracking-wider text-elegant">Allergeni</label>
                                {allergens.length === 0 && (
                                    <span className="text-[10px] uppercase tracking-wider bg-amber-500/20 text-amber-400 border border-amber-500/40 px-1.5 py-0.5 rounded font-bold" data-testid="dish-allergens-warn">
                                        ⚠ Da compilare (obbligatori sul menu)
                                    </span>
                                )}
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {allergenOptions.map((a) => (
                                    <button
                                        key={a.id}
                                        type="button"
                                        onClick={() => toggleAllergen(a.id)}
                                        className={`text-xs px-2.5 py-1 rounded-md border transition ${
                                            allergens.includes(a.id)
                                                ? "bg-brand text-deep border-brand font-bold"
                                                : "bg-surface text-elegant border-[#1f2c44] hover:border-brand"
                                        }`}
                                    >
                                        {a.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    <label className="flex items-center gap-2 text-sm text-elegant cursor-pointer">
                        <input type="checkbox" checked={available} onChange={(e) => setAvailable(e.target.checked)} className="accent-brand" />
                        Disponibile (visibile ai camerieri)
                    </label>
                </div>
                <div className="px-5 py-3 border-t border-[#1f2c44] flex justify-end gap-2">
                    <button onClick={onClose} className="h-10 px-4 border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand rounded-md text-xs uppercase tracking-wider">Annulla</button>
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="save-dish"
                        className="h-10 px-5 bg-brand hover:bg-brand-light text-deep font-bold rounded-md text-xs uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"
                    >
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        {isEdit ? "Salva" : "Crea"}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// TAB 3 — LOGO & BRAND
// ============================================================================
const BrandTab = () => {
    const [settings, setSettings] = useState({ name: "", logo_base64: "", primary_color: "#0b1220", accent_color: "#c0a878" });
    const [saving, setSaving] = useState(false);
    const fileRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get("/restaurant-settings");
            setSettings({
                name: data.name || "",
                logo_base64: data.logo_base64 || "",
                primary_color: data.primary_color || "#0b1220",
                accent_color: data.accent_color || "#c0a878",
            });
        } catch (e) { /* no-op */ }
    };
    useEffect(() => { load(); }, []);

    const handleFile = (file) => {
        if (!file) return;
        if (file.size > 2_500_000) { toast.error("File troppo grande (max 2 MB)"); return; }
        if (!/image\/(png|jpe?g|webp|svg\+xml)/.test(file.type)) {
            toast.error("Usa PNG, JPG, WebP o SVG"); return;
        }
        const reader = new FileReader();
        reader.onload = () => setSettings((s) => ({ ...s, logo_base64: String(reader.result) }));
        reader.readAsDataURL(file);
    };

    const save = async () => {
        try {
            setSaving(true);
            await api.put("/restaurant-settings", settings);
            toast.success("Impostazioni salvate");
        } catch (e) { toast.error(formatError(e)); }
        finally { setSaving(false); }
    };

    return (
        <div className="space-y-5 max-w-2xl">
            <div className="bg-surface border border-[#1f2c44] rounded-lg p-5 space-y-4">
                <div>
                    <Label>Nome del ristorante (sul PDF)</Label>
                    <Input
                        value={settings.name}
                        onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                        placeholder="es. Trattoria Da Mario"
                        testid="brand-name"
                    />
                </div>

                <div>
                    <Label>Logo</Label>
                    <div className="flex items-center gap-4 flex-wrap">
                        <div className="w-28 h-28 bg-deep border border-[#1f2c44] rounded-md flex items-center justify-center overflow-hidden">
                            {settings.logo_base64 ? (
                                <img src={settings.logo_base64} alt="logo" className="max-w-full max-h-full object-contain" data-testid="logo-preview" />
                            ) : (
                                <ImageIcon className="w-8 h-8 text-elegant opacity-40" />
                            )}
                        </div>
                        <div className="flex flex-col gap-2">
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                onChange={(e) => handleFile(e.target.files?.[0])}
                                className="hidden"
                                data-testid="logo-input"
                            />
                            <button
                                onClick={() => fileRef.current?.click()}
                                className="h-10 px-4 bg-brand hover:bg-brand-light text-deep font-bold rounded-md text-xs uppercase tracking-wider flex items-center gap-2"
                                data-testid="upload-logo-btn"
                            >
                                <ImageIcon className="w-3.5 h-3.5" /> Carica logo
                            </button>
                            {settings.logo_base64 && (
                                <button
                                    onClick={() => setSettings({ ...settings, logo_base64: "" })}
                                    className="h-9 px-3 border border-[#1f2c44] hover:border-red-500/60 text-elegant hover:text-red-400 rounded-md text-xs uppercase tracking-wider"
                                    data-testid="remove-logo-btn"
                                >
                                    Rimuovi
                                </button>
                            )}
                            <div className="text-[10px] text-elegant">PNG / JPG / WebP / SVG — max 2 MB</div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label>Colore primario (titoli/prezzi PDF)</Label>
                        <div className="flex items-center gap-2">
                            <input
                                type="color"
                                value={settings.primary_color}
                                onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                                className="h-10 w-12 rounded border border-[#1f2c44] bg-deep cursor-pointer"
                                data-testid="primary-color"
                            />
                            <Input
                                value={settings.primary_color}
                                onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                                placeholder="#0b1220"
                            />
                        </div>
                    </div>
                    <div>
                        <Label>Colore d'accento (sezioni PDF)</Label>
                        <div className="flex items-center gap-2">
                            <input
                                type="color"
                                value={settings.accent_color}
                                onChange={(e) => setSettings({ ...settings, accent_color: e.target.value })}
                                className="h-10 w-12 rounded border border-[#1f2c44] bg-deep cursor-pointer"
                                data-testid="accent-color"
                            />
                            <Input
                                value={settings.accent_color}
                                onChange={(e) => setSettings({ ...settings, accent_color: e.target.value })}
                                placeholder="#c0a878"
                            />
                        </div>
                    </div>
                </div>

                <div className="pt-2 border-t border-[#1f2c44] flex justify-end">
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="save-brand"
                        className="h-11 px-6 bg-brand hover:bg-brand-light text-deep font-bold rounded-md text-sm uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Salva
                    </button>
                </div>
            </div>

            <div className="bg-surface border border-[#1f2c44] rounded-lg p-5 text-xs text-elegant">
                <div className="font-display text-warm text-sm font-bold mb-2 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-brand" /> Suggerimenti
                </div>
                <ul className="space-y-1 list-disc list-inside">
                    <li>Logo ideale: PNG trasparente quadrato, almeno 400×400 px.</li>
                    <li>Il colore primario verrà usato per il nome del ristorante e i prezzi nel PDF.</li>
                    <li>Il colore d'accento verrà usato per i titoli delle sezioni (es. "Antipasti", "Primi").</li>
                    <li>I PDF generati non contengono indirizzo o foto piatti — solo logo, nome locale e contenuti del menu.</li>
                </ul>
            </div>
        </div>
    );
};

// ============================================================================
// SHARED
// ============================================================================
const Label = ({ children }) => (
    <label className="block text-xs uppercase tracking-wider text-elegant mb-1.5">{children}</label>
);
const Input = ({ testid, ...rest }) => (
    <input
        {...rest}
        data-testid={testid}
        className="w-full h-10 bg-surface border border-[#1f2c44] focus:border-brand text-warm rounded-md px-3 text-sm outline-none"
    />
);

export default MenuDelGiorno;
